import React from "react";
import { useTranslation } from "react-i18next";

import { Divider, Select, Typography, Card, Space } from "antd";
import { FileTextOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

import { OrderTable } from "./OrderTable";
import { useAntdTheme } from "../../shared/hooks";

const { Title, Text } = Typography;

type ReportsViewProps = {
  restaurantId: string;
};

type TimeRange = {
  startTime: Dayjs;
  endTime: Dayjs;
};

const THIS_MONTH_LABEL_KEY = "current_month_to_date";

function getAllMonths(): Map<string, TimeRange> {
  // Get all months since 2023 Jan as possible choices.
  // 2023 Jan since data that's too old is likely not helpful.
  const choices = new Map<string, TimeRange>();

  const currMonth = dayjs().startOf("month");
  let historicalMonth = dayjs("2023/01/01").startOf("month");

  while (currMonth.isAfter(historicalMonth)) {
    const key = historicalMonth.format("YYYY-MM");
    choices.set(key, {
      startTime: historicalMonth.clone(),
      endTime: historicalMonth.clone().endOf("month"),
    });

    historicalMonth = historicalMonth.add(1, "months");
  }

  choices.set(THIS_MONTH_LABEL_KEY, {
    startTime: currMonth,
    endTime: currMonth.clone().endOf("month"),
  });

  return choices;
}

export const ReportsView = (props: ReportsViewProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const [selectedMonth, setSelectedMonth] = React.useState<string>(THIS_MONTH_LABEL_KEY);

  const months = getAllMonths();
  const monthLabels = Array.from(months)
    .map(([label, t]) => label)
    .sort();

  const timeRange = months.get(selectedMonth);
  if (timeRange == null) {
    // This should never happen.
    throw new Error(`cannot find selected month ${selectedMonth}`);
  }

  return (
    <div>
      {/* Month Selection */}
      <Card
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Space direction="vertical" style={{ width: "100%", gap: token.marginMD }}>
          <Space>
            <CalendarOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Title level={4} style={{ margin: 0 }}>
              {t("select_month")}
            </Title>
          </Space>
          <Select
            value={selectedMonth}
            onChange={setSelectedMonth}
            size="large"
            style={{
              width: "100%",
              maxWidth: "400px",
              borderRadius: token.borderRadiusLG,
            }}
            options={monthLabels.map((label) => ({
              key: label,
              value: label,
              label: label === THIS_MONTH_LABEL_KEY ? t(THIS_MONTH_LABEL_KEY) : label,
            }))}
          />
        </Space>
      </Card>

      {/* Order Table */}
      <Card
        title={
          <Space>
            <FileTextOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("order_report")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <OrderTable
          restaurantId={props.restaurantId}
          start_time={timeRange.startTime}
          end_time={timeRange.endTime}
        />
      </Card>
    </div>
  );
};
